import type { Dream, CreateDreamDto, UpdateDreamDto } from '@/store/types/dream.types'
import type { DreamityGoalResDto, DreamityGoalModifyReqDto, DreamityGoalSaveReqDto } from '@/store/types/goal.types'
import type { DreamityPlanResDto, DreamityPlanModifyReqDto, DreamityPlanSaveReqDto } from '@/store/types/plan.types'
import type { DreamityItemResDto, DreamityItemModifyReqDto, DreamityItemSaveReqDto } from '@/store/types/checklist.types'
import type { DreamityActionResDto, DreamityActionSaveReqDto } from '@/store/types/calendar.types'
import { listOccurrenceYmdsInRange, normalizePlanFrequency } from '@/utils/planActionGenerator'
import type { DreamitySnapshot } from '@/types/dreamitySnapshot.types'
import {
  createEmptyDreamitySnapshot,
  getSnapshotUserIdFromToken,
  readLocalDreamitySnapshot,
  writeLocalDreamitySnapshot,
} from '@/services/dreamityLocalSnapshot'
import { scheduleDebouncedPushDreamitySnapshot } from '@/services/dreamitySyncService'

function newId(prefix: string): string {
  try {
    return `${prefix}_${crypto.randomUUID()}`
  } catch {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  }
}

function touch(snapshot: DreamitySnapshot): DreamitySnapshot {
  return {
    ...snapshot,
    meta: {
      ...snapshot.meta,
      dirty: true,
      localBundleUpdatedAt: new Date().toISOString(),
    },
  }
}

function load(userId: string): DreamitySnapshot {
  return readLocalDreamitySnapshot(userId) ?? createEmptyDreamitySnapshot()
}

function save(userId: string, snapshot: DreamitySnapshot): void {
  writeLocalDreamitySnapshot(userId, snapshot)
  scheduleDebouncedPushDreamitySnapshot()
}

function withUser<T>(fn: (userId: string) => T): T | null {
  const userId = getSnapshotUserIdFromToken()
  if (!userId) return null
  return fn(userId)
}

function dreamNames(snapshot: DreamitySnapshot): Map<string, string> {
  const m = new Map<string, string>()
  for (const d of snapshot.domains.dreams ?? []) {
    m.set(d.dreamId, d.dreamName)
  }
  return m
}

function goalNames(snapshot: DreamitySnapshot): Map<string, string> {
  const m = new Map<string, string>()
  for (const g of snapshot.domains.goals ?? []) {
    m.set(g.goalId, g.goalName)
  }
  return m
}

function planNames(snapshot: DreamitySnapshot): Map<string, string> {
  const m = new Map<string, string>()
  for (const p of snapshot.domains.plans ?? []) {
    m.set(p.planId, p.planName)
  }
  return m
}

export const dreamityPlannerRepository = {
  listDreams(): Dream[] {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return []
    const snap = load(userId)
    const dreams = snap.domains.dreams ?? []
    const plans = snap.domains.plans ?? []
    const items = snap.domains.items ?? []

    return dreams.map(dream => {
      const dreamId = dream.dreamId
      const dreamPlans = plans.filter(p => p.dreamId === dreamId)
      const dreamItems = items.filter(i => i.dreamId === dreamId)
      
      // Calculate total item price
      const totalItemPrice = dreamItems.reduce((sum, item) => sum + (item.itemPrice || 0), 0)
      
      // Calculate completion rate based on plans (as per user request)
      let completionRate = 0
      if (dreamPlans.length > 0) {
        const completedPlans = dreamPlans.filter(p => p.isCompleted).length
        completionRate = completedPlans / dreamPlans.length
      }

      return {
        ...dream,
        totalItemPrice,
        completionRate: completionRate > 0 ? completionRate : (dream.completionRate || 0)
      }
    })
  },

  getDream(dreamId: string): Dream | undefined {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return undefined
    const snap = load(userId)
    const dream = snap.domains.dreams?.find((d) => d.dreamId === dreamId)
    if (!dream) return undefined

    const plans = snap.domains.plans ?? []
    const items = snap.domains.items ?? []
    
    const dreamPlans = plans.filter(p => p.dreamId === dreamId)
    const dreamItems = items.filter(i => i.dreamId === dreamId)
    
    const totalItemPrice = dreamItems.reduce((sum, item) => sum + (item.itemPrice || 0), 0)
    
    let completionRate = 0
    if (dreamPlans.length > 0) {
      const completedPlans = dreamPlans.filter(p => p.isCompleted).length
      completionRate = completedPlans / dreamPlans.length
    }

    return {
      ...dream,
      totalItemPrice,
      completionRate: completionRate > 0 ? completionRate : (dream.completionRate || 0)
    }
  },

  createDream(dto: CreateDreamDto): Dream {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) throw new Error('Not authenticated')
    const snap = load(userId)
    const dream: Dream = {
      dreamId: newId('dream'),
      dreamName: dto.dreamName,
      dreamDesc: dto.dreamDesc,
      startDate: dto.startDate,
      endDate: dto.endDate,
      budget: dto.budget,
      categoryNames: dto.categories.length
        ? dto.categories.map((c) => c.categoryName)
        : null,
      isShared: false,
      completionRate: null,
    }
    const dreams = [...(snap.domains.dreams ?? []), dream]
    save(userId, touch({ ...snap, domains: { ...snap.domains, dreams } }))
    return dream
  },

  updateDream(dreamId: string, dto: UpdateDreamDto): Dream | null {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return null
    const snap = load(userId)
    const dreams = snap.domains.dreams ?? []
    const idx = dreams.findIndex((d) => d.dreamId === dreamId)
    if (idx < 0) return null
    const prev = dreams[idx]
    const next: Dream = {
      ...prev,
      dreamName: dto.dreamName,
      dreamDesc: dto.dreamDesc,
      startDate: dto.startDate,
      endDate: dto.endDate,
      budget: dto.budget,
      categoryNames: dto.categories.length ? dto.categories.map((c) => c.categoryName) : null,
      completionRate: dto.completionRate ?? prev.completionRate,
    }
    const nextDreams = [...dreams]
    nextDreams[idx] = next
    save(userId, touch({ ...snap, domains: { ...snap.domains, dreams: nextDreams } }))
    return next
  },

  deleteDream(dreamId: string): void {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return
    const snap = load(userId)
    const goalIds = new Set((snap.domains.goals ?? []).filter((g) => g.dreamId === dreamId).map((g) => g.goalId))
    const plans = (snap.domains.plans ?? []).filter((p) => !goalIds.has(p.goalId))
    const planIdsRemoved = new Set(
      (snap.domains.plans ?? []).filter((p) => goalIds.has(p.goalId)).map((p) => p.planId)
    )
    const items = (snap.domains.items ?? []).filter((i) => !planIdsRemoved.has(i.planId))
    const actions = (snap.domains.actions ?? []).filter((a) => a.dreamId !== dreamId)
    const goals = (snap.domains.goals ?? []).filter((g) => g.dreamId !== dreamId)
    const dreams = (snap.domains.dreams ?? []).filter((d) => d.dreamId !== dreamId)
    save(userId, touch({ ...snap, domains: { ...snap.domains, dreams, goals, plans, items, actions } }))
  },

  listGoals(dreamId: string): DreamityGoalResDto[] {
    return withUser((uid) => (load(uid).domains.goals ?? []).filter((g) => g.dreamId === dreamId)) ?? []
  },

  createGoal(dto: DreamityGoalSaveReqDto): DreamityGoalResDto {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) throw new Error('Not authenticated')
    const snap = load(userId)
    const goal: DreamityGoalResDto = {
      goalId: newId('goal'),
      dreamId: dto.dreamId,
      goalName: dto.goalName,
    }
    const goals = [...(snap.domains.goals ?? []), goal]
    save(userId, touch({ ...snap, domains: { ...snap.domains, goals } }))
    return goal
  },

  updateGoal(goalId: string, dto: DreamityGoalModifyReqDto): void {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return
    const snap = load(userId)
    const goals = (snap.domains.goals ?? []).map((g) =>
      g.goalId === goalId ? { ...g, goalName: dto.goalName } : g
    )
    save(userId, touch({ ...snap, domains: { ...snap.domains, goals } }))
  },

  deleteGoal(goalId: string): void {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return
    const snap = load(userId)
    const planIds = new Set((snap.domains.plans ?? []).filter((p) => p.goalId === goalId).map((p) => p.planId))
    const plans = (snap.domains.plans ?? []).filter((p) => p.goalId !== goalId)
    const items = (snap.domains.items ?? []).filter((i) => !planIds.has(i.planId))
    const actions = (snap.domains.actions ?? []).filter((a) => a.goalId !== goalId)
    const goals = (snap.domains.goals ?? []).filter((g) => g.goalId !== goalId)
    save(userId, touch({ ...snap, domains: { ...snap.domains, goals, plans, items, actions } }))
  },

  listPlans(dreamId: string, goalId: string): DreamityPlanResDto[] {
    return (
      withUser((uid) =>
        (load(uid).domains.plans ?? []).filter((p) => String(p.dreamId) === String(dreamId) && String(p.goalId) === String(goalId))
      ) ?? []
    )
  },

  createPlan(dreamId: string, dto: DreamityPlanSaveReqDto): DreamityPlanResDto {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) throw new Error('Not authenticated')
    const snap = load(userId)
    const normFreq = normalizePlanFrequency(dto.frequency, {
      startDate: dto.startDate,
      endDate: dto.endDate,
    })
    const endDate = normFreq === 'NONE' ? dto.startDate : dto.endDate
    const plan: DreamityPlanResDto = {
      planId: newId('plan'),
      goalId: dto.goalId,
      dreamId,
      planName: dto.planName,
      planDesc: dto.planDesc,
      frequency: normFreq,
      frequencyDay: dto.frequencyDay,
      isCompleted: dto.isCompleted,
      startDate: dto.startDate,
      endDate,
    }
    const plans = [...(snap.domains.plans ?? []), plan]
    save(userId, touch({ ...snap, domains: { ...snap.domains, plans } }))
    return plan
  },

  updatePlan(planId: string, dto: DreamityPlanModifyReqDto): DreamityPlanResDto | null {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return null
    const snap = load(userId)
    const plans = snap.domains.plans ?? []
    const idx = plans.findIndex((p) => p.planId === planId)
    if (idx < 0) return null
    const prev = plans[idx]
    const normFreq = normalizePlanFrequency(dto.frequency, {
      startDate: dto.startDate,
      endDate: dto.endDate,
    })
    const endDate = normFreq === 'NONE' ? dto.startDate : dto.endDate
    const next: DreamityPlanResDto = {
      ...prev,
      planName: dto.planName,
      planDesc: dto.planDesc,
      frequency: normFreq,
      frequencyDay: dto.frequencyDay,
      isCompleted: dto.isCompleted,
      startDate: dto.startDate,
      endDate,
    }
    const nextPlans = [...plans]
    nextPlans[idx] = next
    save(userId, touch({ ...snap, domains: { ...snap.domains, plans: nextPlans } }))
    return next
  },

  deletePlan(planId: string): void {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return
    const snap = load(userId)
    const plans = (snap.domains.plans ?? []).filter((p) => p.planId !== planId)
    const items = (snap.domains.items ?? []).filter((i) => i.planId !== planId)
    const actions = (snap.domains.actions ?? []).filter((a) => a.planId !== planId)
    save(userId, touch({ ...snap, domains: { ...snap.domains, plans, items, actions } }))
  },

  getPlan(planId: string): DreamityPlanResDto | undefined {
    return withUser((uid) => load(uid).domains.plans?.find((p) => p.planId === planId)) ?? undefined
  },

  listItems(dreamId: string, planId: string): DreamityItemResDto[] {
    return (
      withUser((uid) =>
        (load(uid).domains.items ?? []).filter((i) => String(i.planId) === String(planId) && String(i.dreamId) === String(dreamId))
      ) ?? []
    )
  },

  createItem(dreamId: string, dto: DreamityItemSaveReqDto): DreamityItemResDto {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) throw new Error('Not authenticated')
    const snap = load(userId)
    const item: DreamityItemResDto = {
      itemId: newId('item'),
      planId: dto.planId,
      dreamId,
      goalId: dto.goalId,
      itemName: dto.itemName,
      itemDesc: dto.itemDesc,
      itemLink: dto.itemLink,
      itemPrice: dto.itemPrice,
      isCompleted: dto.isCompleted,
    }
    const items = [...(snap.domains.items ?? []), item]
    save(userId, touch({ ...snap, domains: { ...snap.domains, items } }))
    return item
  },

  updateItem(itemId: string, dto: DreamityItemModifyReqDto): DreamityItemResDto | null {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return null
    const snap = load(userId)
    const items = snap.domains.items ?? []
    const idx = items.findIndex((i) => i.itemId === itemId)
    if (idx < 0) return null
    const prev = items[idx]
    const next: DreamityItemResDto = {
      ...prev,
      itemName: dto.itemName,
      itemDesc: dto.itemDesc,
      itemLink: dto.itemLink,
      itemPrice: dto.itemPrice,
      isCompleted: dto.isCompleted,
    }
    const nextItems = [...items]
    nextItems[idx] = next
    save(userId, touch({ ...snap, domains: { ...snap.domains, items: nextItems } }))
    return next
  },

  deleteItem(itemId: string): void {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return
    const snap = load(userId)
    const items = (snap.domains.items ?? []).filter((i) => i.itemId !== itemId)
    save(userId, touch({ ...snap, domains: { ...snap.domains, items } }))
  },

  listActionsInRange(startYyyymmdd: string, endYyyymmdd: string): DreamityActionResDto[] {
    return (
      withUser((uid) => {
        const snap = load(uid)
        const actions = snap.domains.actions ?? []
        const plans = snap.domains.plans ?? []

        const inRangeStored = actions.filter((a) => {
          if (a.isDeleted) return false
          const d = a.actionDate.replace(/-/g, '')
          return d >= startYyyymmdd && d <= endYyyymmdd
        })

        const rangeYmd = (compact: string) =>
          compact.length === 8 && !compact.includes('-')
            ? `${compact.slice(0, 4)}-${compact.slice(4, 6)}-${compact.slice(6, 8)}`
            : compact

        const startYmd = rangeYmd(startYyyymmdd)
        const endYmd = rangeYmd(endYyyymmdd)

        const dn = dreamNames(snap)
        const gn = goalNames(snap)
        const pn = planNames(snap)

        const covered = new Set(
          inRangeStored.map((a) => `${a.planId}|${a.actionDate.replace(/-/g, '')}`)
        )

        const virtual: DreamityActionResDto[] = []
        for (const plan of plans) {
          if (plan.isCompleted) continue
          const ymds = listOccurrenceYmdsInRange(plan, startYmd, endYmd)
          for (const ymd of ymds) {
            const compact = ymd.replace(/-/g, '')
            const key = `${plan.planId}|${compact}`
            if (covered.has(key)) continue
            covered.add(key)
            virtual.push({
              actionId: `virtual:${plan.planId}:${ymd}`,
              planId: plan.planId,
              dreamId: plan.dreamId,
              goalId: plan.goalId,
              dreamName: dn.get(plan.dreamId),
              goalName: gn.get(plan.goalId),
              planName: pn.get(plan.planId),
              actionDate: ymd,
              actionTime: '09:00:00',
              actionName: plan.planName,
              actionDesc: plan.planDesc || plan.planName,
              isCompleted: false,
              frequency: plan.frequency,
            })
          }
        }

        const merged = [...inRangeStored, ...virtual]
        merged.sort((a, b) => {
          const da = a.actionDate.replace(/-/g, '')
          const db = b.actionDate.replace(/-/g, '')
          if (da !== db) return da.localeCompare(db)
          const ta = (a.actionTime || '00:00:00').slice(0, 8)
          const tb = (b.actionTime || '00:00:00').slice(0, 8)
          if (ta !== tb) return ta.localeCompare(tb)
          return (a.actionName || '').localeCompare(b.actionName || '')
        })
        return merged
      }) ?? []
    )
  },

  getAction(dreamId: string, actionId: string): DreamityActionResDto | undefined {
    const found = withUser((uid) =>
      load(uid).domains.actions?.find((a) => a.dreamId === dreamId && a.actionId === actionId)
    )
    return found ?? undefined
  },

  createAction(dreamId: string, planId: string, dto: DreamityActionSaveReqDto): void {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return
    const snap = load(userId)
    const plan = snap.domains.plans?.find((p) => p.planId === planId)
    if (!plan) return
    const dn = dreamNames(snap)
    const gn = goalNames(snap)
    const pn = planNames(snap)
    const action: DreamityActionResDto = {
      actionId: newId('action'),
      planId,
      dreamId,
      goalId: plan.goalId,
      dreamName: dn.get(dreamId),
      goalName: gn.get(plan.goalId),
      planName: pn.get(planId),
      actionDate: dto.actionDate,
      actionTime: dto.actionTime,
      actionName: dto.actionName,
      actionDesc: dto.actionDesc,
      isCompleted: dto.isCompleted,
      frequency: plan.frequency,
    }
    const actions = [...(snap.domains.actions ?? []), action]
    save(userId, touch({ ...snap, domains: { ...snap.domains, actions } }))
  },

  updateAction(
    dreamId: string,
    planId: string,
    actionId: string | null,
    dto: DreamityActionSaveReqDto
  ): void {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return
    const snap = load(userId)
    const actions = [...(snap.domains.actions ?? [])]
    const idx = actions.findIndex(
      (a) =>
        a.dreamId === dreamId &&
        a.planId === planId &&
        (actionId == null ? a.actionId == null : a.actionId === actionId)
    )
    if (idx < 0) return
    const prev = actions[idx]
    actions[idx] = {
      ...prev,
      actionDate: dto.actionDate,
      actionTime: dto.actionTime,
      actionName: dto.actionName,
      actionDesc: dto.actionDesc,
      isCompleted: dto.isCompleted,
    }
    save(userId, touch({ ...snap, domains: { ...snap.domains, actions } }))
  },

  deleteAction(dreamId: string, actionId: string): void {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return
    const snap = load(userId)
    const actions = (snap.domains.actions ?? []).filter(
      (a) => !(a.dreamId === dreamId && a.actionId === actionId)
    )
    save(userId, touch({ ...snap, domains: { ...snap.domains, actions } }))
  },

  /** 통계·대시보드: 스냅샷 도메인 일괄 읽기 (메모리만, 네트워크 없음) */
  readDomainsForDashboard(): {
    dreams: Dream[]
    goals: DreamityGoalResDto[]
    plans: DreamityPlanResDto[]
    items: DreamityItemResDto[]
    actions: DreamityActionResDto[]
  } | null {
    return (
      withUser((uid) => {
        const snap = load(uid)
        return {
          dreams: snap.domains.dreams ?? [],
          goals: snap.domains.goals ?? [],
          plans: snap.domains.plans ?? [],
          items: snap.domains.items ?? [],
          actions: snap.domains.actions ?? [],
        }
      }) ?? null
    )
  },

  /** 플랜 빈도 변경 시 해당 플랜 액션의 frequency 표시용 */
  syncActionFrequencyForPlan(planId: string, frequency: DreamityPlanResDto['frequency']): void {
    const userId = getSnapshotUserIdFromToken()
    if (!userId) return
    const snap = load(userId)
    const actions = (snap.domains.actions ?? []).map((a) =>
      a.planId === planId ? { ...a, frequency } : a
    )
    save(userId, touch({ ...snap, domains: { ...snap.domains, actions } }))
  },
}
